import './imports/bootstrap'
import './imports/graphics'
import './imports/highcharts'

import 'vue-loading-overlay/dist/vue-loading.css'
import 'vue-search-select/dist/VueSearchSelect.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import i18n from './imports/i18n'

import Vue from 'vue'
import App from './App.vue'
import router, { routerAssetClass } from './router/router'
import store from './store/main'
import vueHeadful from 'vue-headful'
import EventBus from './imports/eventBus'
import FairmatComponents from '@fairmat/vue-components'

import VueScrollTo from 'vue-scrollto'
import Loader from 'vue-loading-overlay'
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min'

import { indicators } from './utils/indicators'
import { debugInfo } from './utils/mirageUtils'
import { base64UrlEncode } from './utils/sanitizer'
import { assetClassStorageKey } from './utils/assetClassState'
import { planAssetClassUrl } from './utils/assetClassEntry'
import { consumeAssetClassNotice, writeAssetClassNotice } from './utils/assetClassNotice'
import { RecycleScroller } from 'vue-virtual-scroller'
import { fetchCustomMethod, get as _get, post as _post, postFormData as _postFormData } from './utils/fetcher'

import authentication, {
  doLogout,
  initOptions,
  parseToken,
  getUserInfo,
  setUserInfo,
  setAccessToken,
  setRefreshToken,
  setIdToken,
  getRealm,
} from './plugins/authentication'
import permissions from './plugins/permissions'

import { track, DIMENSION, installTrackingDirectives, installTrackingPlugin } from './plugins/tracking'
import { installRouterTracking } from './plugins/tracking.router'
import { installStoreTracking } from './plugins/tracking.store'

Vue.config.productionTip = false

Vue.use(EventBus)
Vue.use(VueScrollTo)
Vue.use(authentication)
Vue.use(permissions)
Vue.use(FairmatComponents)
Vue.component('RecycleScroller', RecycleScroller)
Vue.component('loading', Loader)
Vue.component('VueSlider', VueSlider)
Vue.component('vue-headful', vueHeadful)

Vue.directive('visible', function (el, binding) {
  el.style.visibility = !!binding.value ? 'visible' : 'hidden'
})

Vue.directive('click-outside', {
  bind: function (el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      // here I check that click was outside the el and his childrens
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        vnode.context[binding.expression](event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind: function (el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
})

// Analytics (Matomo) — fully isolated & fail-open. Each installer swallows its
// own errors; the extra try/catch guarantees a tracking bug can never stop the
// app from booting. See docs/matomo-tracking-plan.md.
try {
  installTrackingDirectives(Vue)
  installTrackingPlugin(Vue)
  installRouterTracking(router)
  installStoreTracking(store)
  track.measureInitialLoad()
} catch (e) {
  if (process.env.NODE_ENV === 'development') console.warn('[tracking] init skipped', e)
}

if (process.env.VUE_APP_MIRAGE_API_ENABLE === 'true') {
  // Mirage Dynamic import
  import('../mirage-mock/server.js').then((server) => {
    server.makeServer()
  })
  console.log('-- MIRAGE.JS SERVER START --')
}

// Debugging info
if (process.env.NODE_ENV !== 'production') {
  debugInfo()
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/release-note-add-token.js')
      .then((registration) => {
        return registration
      })
      .catch((error) => {
        console.info('ServiceWorker registration failed: ', error)
      })
  })
}

const CONNECTION_ERROR_TOAST_ID = 'connection-error'

Vue.mixin({
  computed: {
    isAuth: {
      get() {
        return this.$keycloak.authenticated
      },
    },
    authUsername: {
      get() {
        const info = JSON.parse(getUserInfo())
        return info.username
      },
    },

    indicatorsConfig: {
      get() {
        return indicators
      },
    },

    isInDebug: {
      get() {
        return this.getEnv() === 'development'
      },
    },

    isInDemoEnv: {
      get() {
        return location.hostname.includes('openkid-demo') || location.hostname.includes('localhost')
      },
    },

    filterSelectValues: function () {
      let filtersMessages = this.$i18n.messages[this.$i18n.locale].search.filterSelectValues
      let horizons = this.$store.getters.getTimeHorizonIntervalsList
      if (horizons.length > 0) {
        const keys = Object.keys(filtersMessages.timeHorizon)
        keys.map((key) => {
          let from = horizons.find((el) => el.id === Number(key)).from || ''
          let to = horizons.find((el) => el.id === Number(key)).to || ''
          // adding +1 to long term (id = 8)
          filtersMessages.timeHorizon[key] = this.$tc('search.filterSelectValues.timeHorizon.' + key, 0, {
            n: Number(key) === 8 ? from + 1 : from,
            z: to,
          })
        })
      }
      return {
        ratingProfiles: [],
        ...filtersMessages,
        sriOptions: [1, 2, 3, 4, 5, 6, 7].map((x) => {
          return { text: x.toString(), value: x }
        }),
      }
    },

    getMarketComparationIndices: function () {
      const obj = {}
      const marketIndices = this.$i18n.messages[this.$i18n.locale].marketComparation.indices
      const indicesKeys = Object.keys(marketIndices)
      const indicesValues = Object.values(marketIndices)
      indicesKeys.forEach((key, index) => {
        const elKeys = Object.keys(indicesValues[index])
        const elValues = Object.values(indicesValues[index])
        obj[key] = elKeys.map((key, index) => {
          return { value: key, text: elValues[index] }
        })
      })
      return obj
    },

    getMarketComparationIndicesForReturnRiskCost: function () {
      const obj = {}
      const marketIndices = this.$i18n.messages[this.$i18n.locale].marketComparation.returnRiskCostChart
      const indicesKeys = Object.keys(marketIndices)
      const indicesValues = Object.values(marketIndices)
      indicesKeys.forEach((key, index) => {
        const elKeys = Object.keys(indicesValues[index])
        const elValues = Object.values(indicesValues[index])
        obj[key] = elKeys.map((key, index) => {
          return { value: key, text: elValues[index] }
        })
      })
      return obj
    },
  },

  methods: {
    getEnv() {
      return process.env.NODE_ENV
    },

    get(url, data) {
      return _get(url, data)
    },

    post(url, data) {
      return _post(url, data)
    },

    postFormData(url, data) {
      return _postFormData(url, data)
    },

    delete(url, data) {
      return fetchCustomMethod(url, 'DELETE', data)
    },

    openProductDetails(item, currentProfileId, forceNewTab = false) {
      const profileIdKey = assetClassStorageKey('currentScoringProfileId')
      currentProfileId ? localStorage.setItem(profileIdKey, currentProfileId) : localStorage.removeItem(profileIdKey)

      let investmentOptionName = null
      if (item.investmentOptionName) {
        investmentOptionName = base64UrlEncode(item.investmentOptionName)
      }

      if (item.investmentOptionIsin && item.isKIID) {
        investmentOptionName = base64UrlEncode(item.investmentOptionIsin)
      }

      const params = {
        name: 'productDetails',
        params: {
          productName: base64UrlEncode(item.name),
          issuer: item.issuerCode,
          referenceDate: item.referenceDate,
          scoringProfileId: this.$store.getters.currentProfileId,
          investmentOptionName: investmentOptionName,
        },
      }

      // Search results (list + SmartFilter autocomplete) always open the product in a new tab —
      // the user stays on the search page/list — regardless of the dev/prod isInDebug split that
      // every other entry point (ScoringProfiles, Benchmarking, MarketComparation) still follows.
      if (!forceNewTab && this.isInDebug) {
        this.$router.push(params)
      } else {
        const url = this.$router.resolve(params)
        window.open(url.href, '_blank')
      }

      // New-tab opens don't trigger a router navigation in THIS tab, so track
      // them explicitly. Fail-open: never affects the navigation above.
      try {
        track.event('Product', 'open', item && item.issuerCode)
      } catch (e) {
        /* ignore */
      }
    },

    changeFollow(product) {
      const url = product.isFollowed ? '/api/user/unfollow' : '/api/user/follow'
      try {
        track.event('Product', product.isFollowed ? 'unfollow' : 'follow', product && product.issuerCode)
      } catch (e) {
        /* ignore */
      }
      this.post(url, { issuerCode: product.issuerCode, name: product.name }).then(() => {
        product.isFollowed = !product.isFollowed
      })
    },

    httpErrorsHandler(e) {
      if (e == null) {
        return
      }

      // No HTTP response at all — the request never reached the server (offline, dropped
      // connection, CORS). Left unhandled the page simply stops, with no way for the user to tell
      // a failure from a slow answer.
      if (typeof e != 'string' && 'response' in e == false) {
        this.showConnectionError()
        return
      }

      const code = typeof e == 'string' ? e : e.response.status.toString()
      switch (code) {
        case '403':
        case '404':
        case '500':
          this.$router.push({ name: 'http-error', params: { code: code } })
          break
        case '502':
        case '503':
        case '504':
          this.showConnectionError()
          break
        default:
          break
      }
      return
    },

    // A transient network problem must not cost the user the work in progress, so this is a notice
    // and not a redirect to the error page. One notice at a time: requests that fail together —
    // pages do fire several at once — must not stack up identical toasts.
    showConnectionError() {
      this.$bvToast.hide(CONNECTION_ERROR_TOAST_ID)
      this.$bvToast.toast(
        navigator.onLine ? this.$t('common.errors.serverUnreachable') : this.$t('common.errors.offline'),
        {
          id: CONNECTION_ERROR_TOAST_ID,
          title: this.$t('common.errors.connectionErrorTitle'),
          variant: 'danger',
        }
      )
    },

    // Explains, once, why the page a switch (or an entry URL) landed on is not the one asked for.
    showAssetClassNotice() {
      const notice = consumeAssetClassNotice()
      if (notice) {
        this.$bvToast.toast(this.$t(notice.key, notice.params), {
          title: this.$t('navbar.assetClass.notices.title'),
          variant: 'info',
        })
      }
    },

    setPageTitle(pageId, ...extra) {
      return (
        this.$t('pageTitles.appName') +
        ' - ' +
        this.$t('pageTitles.' + pageId) +
        (extra ? extra.reduce((old, curr) => old + ' - ' + curr, '') : '')
      )
    },
  },
})

// Puts the page on the /{assetClass}/ URL of the asset class initAssetClasses settled on, before the
// app mounts (the decision itself is planAssetClassUrl). Returns false when the page is being
// reloaded to get there: the caller must not mount.
function settleAssetClassUrl() {
  const { pathname, search, hash } = window.location
  const plan = planAssetClassUrl({
    selected: store.getters.getSelectedAssetClass,
    routerAssetClass,
    pathname,
    search,
    hash,
  })

  if (plan.action === 'rewrite') {
    window.history.replaceState(null, '', plan.url)
  } else if (plan.action === 'reload') {
    if (store.getters.getRejectedAssetClass) {
      writeAssetClassNotice('navbar.assetClass.notices.assetClassNotAuthorized')
    }
    window.location.replace(plan.url)
    return false
  }
  return true
}

Vue.$keycloak
  .init(initOptions)
  .then((auth) => {
    const accessToken = Vue.$keycloak.token
    const refreshToken = Vue.$keycloak.refreshToken
    const idToken = Vue.$keycloak.idToken

    // Without the return, execution carried on and wrote the string 'undefined' into localStorage,
    // hence 'Bearer undefined' on every later call and a second, cascading logout.
    if (!auth || !accessToken || !refreshToken) {
      doLogout()
      return
    }

    setAccessToken(accessToken)
    setRefreshToken(refreshToken)
    setIdToken(idToken)

    const userInfo = parseToken(accessToken, 'okv_')
    if (userInfo == null) {
      doLogout()
      return
    }
    setUserInfo(JSON.stringify(userInfo))

    // Attribute activity to a pseudonymised user (no PII sent). Fail-open.
    try {
      if (userInfo && userInfo.username) {
        track.setUser(userInfo.username)
        track.dimension(DIMENSION.role, (userInfo.roles || []).join(','))
      }
      // Tenant (realm) tracking: interim "Tenant" event now + custom dimension
      // as soon as its id is configured. Fail-open.
      track.tenant(getRealm())
    } catch (e) {
      /* tracking must never block auth/boot */
    }

    _get('/api/user/info')
      .then((data) => {
        store.commit('setUserPermissions', data)
        store.dispatch('getTimeHorizonIntervals', data.timeHorizonIntervals)
        store.dispatch('initAssetClasses', {
          assetClasses: data.assetClasses,
          scopedApiRoutes: data.scopedApiRoutes,
          defaultAssetClass: data.defaultAssetClass,
          assetClassCounters: data.assetClassCounters,
        })
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (!settleAssetClassUrl()) {
          return
        }
        new Vue({
          el: '#app',
          i18n,
          router,
          store: store,
          render: (h) => h(App),
          mounted() {
            this.showAssetClassNotice()
          },
        })
      })
  })
  .catch((e) => {
    console.error(e)
    doLogout()
  })
